import { useNavigation } from "@react-navigation/native";
import {
  Avatar,
  Button,
  Chat,
  ChatBox,
  KeepAwake,
  Loader,
  Text,
  useAccessStatus,
  useBrandingAsset,
  useChatLockedOut,
  useLivestreamStore,
  useNetworkName,
  useNetworkProfileUrl,
  useSocialShell,
  useTheme,
  useToast,
  useViewTotal,
  View,
  zero,
} from "@streamplace/components";
import {
  relativeAge,
  RichTextMessage,
} from "@streamplace/components/src/components/chat/chat-message";
import { VerifiedBadge } from "@streamplace/components/src/components/chat/verified-badge";
import { useAvatars } from "@streamplace/components/src/hooks/useAvatars";
import { useUnpinChatMessage } from "@streamplace/components/src/livestream-store/hooks";
import { useCanModerate } from "@streamplace/components/src/streamplace-store/moderation";
import { usePDSAgent } from "@streamplace/components/src/streamplace-store/xrpc";
import { EmojiPicker } from "components/emoji-picker/emoji-picker";
import { OAuthLoginButton } from "components/login/oauth-login-button";
import { useStreamMeta } from "components/mobile/bottom-metadata";
import { Player } from "components/mobile/player";
import { PlayerProps } from "components/player/props";
import {
  PhoneMenuButton,
  useOpenExternal,
} from "components/sidebar/sidebar-overlay";
import { MessageIcon } from "components/sidebar/social-icons";
import { FullscreenProvider } from "contexts/FullscreenContext";
import { usePhoneMenu } from "hooks/useSidebarControl";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Eye,
  Pin,
  Share2,
  X,
} from "lucide-react-native";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Linking,
  PanResponder,
  Platform,
  Pressable,
  ScrollView,
  useWindowDimensions,
  type LayoutChangeEvent,
  type TextStyle,
} from "react-native";
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { useStore } from "store";
import { useEmojiData } from "utils/emoji";
import { chatWidthFor, FEED_WIDTH } from "./widths";

/**
 * The "card" stream layout (branding key streamLayout=card): the stream as a
 * post in a 600px feed column with a live-chat column beside it, the way a
 * social timeline presents a video. Two columns from 1040px of content
 * width; below that the chat stacks under the post and the page scrolls.
 *
 * Everything here is chrome around the existing Player, Chat and ChatBox;
 * the video, chat state and moderation are untouched.
 */

function formatPostDate(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${date} at ${time}`;
}

function Hairline({ vertical = false }: { vertical?: boolean }) {
  const { theme } = useTheme();
  return (
    <View
      style={
        vertical
          ? {
              width: 1,
              alignSelf: "stretch",
              backgroundColor: theme.colors.borderSubtle,
            }
          : {
              height: 1,
              width: "100%",
              backgroundColor: theme.colors.borderSubtle,
            }
      }
    />
  );
}

function IconCircleButton({
  onPress,
  label,
  children,
}: {
  onPress: () => void;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Pressable
      accessibilityLabel={label}
      accessibilityRole="button"
      onPress={onPress}
      style={({ hovered }: any) => ({
        width: 33,
        height: 33,
        borderRadius: 999,
        alignItems: "center",
        justifyContent: "center",
        opacity: hovered ? 0.8 : 1,
      })}
    >
      {children}
    </Pressable>
  );
}

function CardHeader() {
  const { theme } = useTheme();
  const navigation: any = useNavigation();
  const phoneMenu = usePhoneMenu();
  const goBack = () => {
    if (navigation.canGoBack()) navigation.goBack();
    else
      navigation.navigate("MainTabs", {
        screen: "HomeTab",
        params: { screen: "HomeMain" },
      });
  };
  return (
    <View
      style={{
        height: 52,
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 20,
        gap: 8,
      }}
    >
      <IconCircleButton onPress={goBack} label="Back">
        <ArrowLeft size={22} color={theme.colors.text2} />
      </IconCircleButton>
      <Text
        weight="semibold"
        style={{ flex: 1, fontSize: 19, lineHeight: 22 }}
        numberOfLines={1}
      >
        Live
      </Text>
      {phoneMenu && <PhoneMenuButton style={{ marginRight: -8 }} />}
    </View>
  );
}

// The feed column's tab bar from the design: Live, Video on demand and Go
// live. It belongs to a stream index page, not to a stream's own page, so
// nothing renders it until that index exists. 48px, 15px labels, a 3px
// brand-colored indicator.
export function CardTabs() {
  const { theme } = useTheme();
  const navigation: any = useNavigation();
  const tabs: { label: string; active?: boolean; onPress: () => void }[] = [
    { label: "Live", active: true, onPress: () => {} },
    {
      label: "Video on demand",
      onPress: () => navigation.navigate("MainTabs", { screen: "VideosTab" }),
    },
    {
      label: "Go live",
      onPress: () =>
        navigation.navigate("MainTabs", {
          screen: "HomeTab",
          params: { screen: "LiveDashboard" },
        }),
    },
  ];
  return (
    <View style={{ flexDirection: "row", height: 48 }}>
      {tabs.map((tab) => (
        <Pressable
          key={tab.label}
          accessibilityRole="tab"
          accessibilityState={{ selected: !!tab.active }}
          onPress={tab.onPress}
          style={({ hovered }: any) => ({
            flex: 1,
            alignItems: "center",
            justifyContent: "flex-end",
            paddingTop: 14,
            opacity: hovered && !tab.active ? 0.85 : 1,
          })}
        >
          <Text
            weight="semibold"
            style={{
              fontSize: 15,
              lineHeight: 20,
              color: tab.active ? theme.colors.text1 : theme.colors.text2,
            }}
          >
            {tab.label}
          </Text>
          <View
            style={{
              marginTop: 10,
              height: 3,
              alignSelf: "stretch",
              marginHorizontal: 16,
              borderRadius: 2,
              backgroundColor: tab.active
                ? theme.colors.primary
                : "transparent",
            }}
          />
        </Pressable>
      ))}
    </View>
  );
}

// A quiet offline state sized to the embed box, in place of the player's
// full-screen offline card (which lays itself out against the window).
function CardOffline({ handle }: { handle: string }) {
  const { theme } = useTheme();
  return (
    <View
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        backgroundColor: theme.colors.surface1,
      }}
    >
      <Text
        weight="semibold"
        style={{ fontSize: 17, lineHeight: 22, textAlign: "center" }}
      >
        {handle} is offline right now
      </Text>
      <Text
        style={{
          fontSize: 15,
          lineHeight: 20,
          marginTop: 6,
          color: theme.colors.text2,
          textAlign: "center",
        }}
      >
        Check back later.
      </Text>
    </View>
  );
}

// The embed: the player in a 16:9 box, rounded inside the feed card and
// edge to edge on phones.
function PlayerEmbed({
  src,
  extraProps,
  onTeleport,
  rounded = true,
}: {
  src: string;
  extraProps: Partial<PlayerProps>;
  onTeleport?: (targetHandle: string, targetDID: string) => void;
  rounded?: boolean;
}) {
  const { handleStr } = useStreamMeta();
  return (
    <View
      style={{
        width: "100%",
        aspectRatio: 16 / 9,
        borderRadius: rounded ? 12 : 0,
        overflow: "hidden",
        backgroundColor: "#000", // token-ok: video letterbox
      }}
    >
      <KeepAwake />
      <FullscreenProvider>
        <Player
          key={src}
          src={src}
          {...extraProps}
          hideChat
          fitContainer
          offlineContent={<CardOffline handle={handleStr} />}
          onTeleport={onTeleport}
        />
      </FullscreenProvider>
    </View>
  );
}

function PostCard({
  src,
  extraProps,
  onTeleport,
  compact = false,
  phone = false,
}: {
  src: string;
  extraProps: Partial<PlayerProps>;
  onTeleport?: (targetHandle: string, targetDID: string) => void;
  /** Streamer row, title and player only: the fixed phone layout keeps the
   *  rest of the height for chat. */
  compact?: boolean;
  /** The phone design: the embed sits above the card edge to edge, so the
   *  card is the streamer row, text, engagement and date alone. */
  phone?: boolean;
}) {
  const { theme } = useTheme();
  const toast = useToast();
  const meta = useStreamMeta();
  const { title, avatarUri, views, handleStr } = meta;
  const viewTotal = useViewTotal();
  // The node reports the streamer's verification, display name and avatar
  // on the profile it sends over the stream's websocket; the profile cache
  // fills any gap the same way chat rows do.
  const streamer = useLivestreamStore((x) => x.profile);
  const streamerDids = useMemo(
    () => (streamer?.did ? [streamer.did] : []),
    [streamer?.did],
  );
  const cached = useAvatars(streamerDids)[streamer?.did ?? ""];
  const displayName =
    meta.displayName || cached?.displayName?.trim() || undefined;
  // The streamer row links to their profile on the network, like a chat
  // row's name does.
  const profileUrlFor = useNetworkProfileUrl();
  const openExternal = useOpenExternal();
  const profileUrl = streamer?.did
    ? profileUrlFor({ handle: streamer.handle, did: streamer.did })
    : undefined;
  // Liveness from the segments actually arriving (a fresh one in the last
  // ten seconds), not the player's mode; no segment at all means offline.
  const segment = useLivestreamStore((x) => x.segment);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const isLive =
    !!segment?.startTime && now - Date.parse(segment.startTime) < 10_000;
  const ls = useLivestreamStore((x) => x.livestream);
  const postDate = formatPostDate((ls?.record as any)?.createdAt);
  const name = displayName || handleStr;

  const share = async () => {
    try {
      if (Platform.OS === "web" && typeof navigator !== "undefined") {
        const url = window.location.href;
        if ((navigator as any).share) {
          await (navigator as any).share({ url });
        } else {
          await navigator.clipboard.writeText(url);
          toast.show("Link copied", undefined, { variant: "success" });
        }
      }
    } catch {
      // user dismissed the share sheet
    }
  };

  return (
    <View style={{ padding: 16, paddingBottom: 0, gap: 0 }}>
      {/* the embed is the player, first (the design's video-first card) */}
      {!phone && (
        <View style={{ marginBottom: 8 }}>
          <PlayerEmbed
            src={src}
            extraProps={extraProps}
            onTeleport={onTeleport}
          />
        </View>
      )}

      {/* streamer row, linking to the profile on the network */}
      <Pressable
        onPress={profileUrl ? () => openExternal(profileUrl) : undefined}
        disabled={!profileUrl}
        accessibilityRole={profileUrl ? "link" : undefined}
        // @ts-ignore renders as <a> on web
        href={profileUrl}
        style={({ hovered }: any) => ({
          flexDirection: "row",
          gap: 12,
          alignItems: "center",
          paddingBottom: 12,
          opacity: hovered ? 0.85 : 1,
        })}
      >
        <Avatar src={avatarUri} name={name} size={42} live={isLive} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Text
              weight="semibold"
              numberOfLines={1}
              style={{ fontSize: 17, lineHeight: 22, flexShrink: 1 }}
            >
              {name}
            </Text>
            <VerifiedBadge author={(streamer as any) ?? {}} size={16} />
          </View>
          {displayName ? (
            <Text
              numberOfLines={1}
              style={{
                fontSize: 15,
                lineHeight: 20,
                color: theme.colors.text2,
              }}
            >
              {handleStr}
            </Text>
          ) : null}
        </View>
      </Pressable>

      {/* the post text under the player, then a hairline, the engagement
          row and the date, each a step tighter than before */}
      {title ? (
        <Text
          numberOfLines={compact ? 2 : undefined}
          style={{ fontSize: 17, lineHeight: 22, paddingBottom: 8 }}
        >
          {title}
        </Text>
      ) : null}
      <Hairline />

      {/* engagement row */}
      {!compact && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 16,
            paddingTop: 8,
            paddingBottom: 2,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
            <Eye
              size={18}
              color={isLive ? theme.colors.live : theme.colors.text3}
            />
            <Text
              style={{
                fontSize: 13,
                lineHeight: 17,
                color: isLive ? theme.colors.live : theme.colors.text3,
              }}
            >
              {typeof views === "number" ? views : 0}
              {isLive ? " watching" : ""}
            </Text>
            {/* the running total of sessions this stream has had, in grey
                beside the live count */}
            {viewTotal !== null && viewTotal > 0 ? (
              <Text
                style={{
                  fontSize: 13,
                  lineHeight: 17,
                  color: theme.colors.text3,
                  marginLeft: 4,
                }}
              >
                {"· " + viewTotal.toLocaleString() + " views"}
              </Text>
            ) : null}
          </View>
          {isLive ? (
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
            >
              <View
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: theme.colors.live,
                }}
              />
              <Text weight="semibold" style={{ fontSize: 13, lineHeight: 17 }}>
                Live
              </Text>
            </View>
          ) : (
            <Text
              style={{
                fontSize: 13,
                lineHeight: 17,
                color: theme.colors.text3,
              }}
            >
              Offline
            </Text>
          )}
          <View style={{ flex: 1 }} />
          <IconCircleButton onPress={share} label="Share">
            <Share2 size={18} color={theme.colors.text3} />
          </IconCircleButton>
        </View>
      )}

      {postDate && !compact ? (
        <Text
          style={{
            fontSize: 13,
            lineHeight: 17,
            color: theme.colors.text2,
            paddingTop: 10,
            paddingBottom: 12,
          }}
        >
          {postDate}
        </Text>
      ) : (
        <View style={{ height: 12 }} />
      )}
    </View>
  );
}

// The pinned message: a filled card one surface step above its container
// (the design's #1d2433 on the page, #272f43 inside the phone chat sheet).
// `collapsible` is the phone sheet's variant: no avatar, and a chevron that
// folds it to one row ("Pinned by …" and a line of text); tapping the folded
// row opens it again. Open by default.
function PinnedCard({
  raised = false,
  collapsible = false,
}: {
  raised?: boolean;
  collapsible?: boolean;
}) {
  const { theme } = useTheme();
  const toast = useToast();
  const pinned = useLivestreamStore((x) => x.pinnedComment);
  const streamerDID = useLivestreamStore((x) => x.profile?.did);
  const message: any = pinned?.message;
  const author = message?.author ?? {};
  // The pinned record carries the author's handle but not avatar or
  // display name; the profile cache fills them in like chat rows do.
  const dids = useMemo(() => (author.did ? [author.did] : []), [author.did]);
  const profile = useAvatars(dids)[author.did];
  // The streamer and moderators with the pin permission can take it down.
  const canUnpin = !!useCanModerate(streamerDID ?? "")?.canPin;
  const unpin = useUnpinChatMessage();
  const [open, setOpen] = useState(true);
  const onUnpin = async () => {
    if (!pinned?.uri || !streamerDID) return;
    try {
      await unpin(pinned.uri, streamerDID);
    } catch (e) {
      toast.show(
        "Couldn't unpin",
        e instanceof Error ? e.message : "Failed to unpin",
        { variant: "error" },
      );
    }
  };
  if (!message) return null;
  const avatar: string | undefined = profile?.avatar || author.avatar;
  const name: string =
    author.displayName || profile?.displayName || author.handle || "";
  const handle: string = author.handle ? `@${author.handle}` : "";
  const text: string = message.record?.text ?? "";
  const facets = message.record?.facets ?? [];
  const age = message.record?.createdAt
    ? relativeAge(message.record.createdAt)
    : "";
  const folded = collapsible && !open;

  const unpinButton = canUnpin ? (
    <Pressable
      onPress={onUnpin}
      accessibilityRole="button"
      accessibilityLabel="Unpin message"
      hitSlop={8}
      style={({ hovered }: any) => ({
        width: 24,
        height: 24,
        borderRadius: 999,
        alignItems: "center",
        justifyContent: "center",
        opacity: hovered ? 1 : 0.7,
      })}
    >
      <X size={14} color={theme.colors.text2} />
    </Pressable>
  ) : null;

  const nameLine = (
    <Text numberOfLines={1} style={{ fontSize: 15, lineHeight: 23 }}>
      <Text weight="semibold" style={{ fontSize: 15 }}>
        {name}
      </Text>
      <VerifiedBadge author={author} profile={profile} size={14} />
      {handle && name !== author.handle ? (
        <Text style={{ fontSize: 15, color: theme.colors.text2 }}>
          {"  " + handle}
        </Text>
      ) : null}
      {age ? (
        <Text style={{ fontSize: 15, color: theme.colors.text2 }}>
          {" · " + age}
        </Text>
      ) : null}
    </Text>
  );

  if (collapsible) {
    return (
      <Pressable
        onPress={folded ? () => setOpen(true) : undefined}
        disabled={!folded}
        accessibilityRole={folded ? "button" : undefined}
        accessibilityLabel={folded ? "Open pinned message" : undefined}
        style={{
          backgroundColor: raised
            ? theme.colors.surface2
            : theme.colors.surface1,
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            paddingLeft: 8,
            paddingRight: 8,
            paddingTop: 8,
          }}
        >
          <Pin size={16} color={theme.colors.text3} />
          <Text
            weight="medium"
            numberOfLines={1}
            style={{
              flex: 1,
              fontSize: 13,
              lineHeight: 17,
              color: theme.colors.text2,
            }}
          >
            {folded
              ? `Pinned by ${name}${handle ? " " + handle : ""}`
              : "Pinned"}
          </Text>
          {unpinButton}
          <Pressable
            onPress={() => setOpen(!open)}
            accessibilityRole="button"
            accessibilityLabel={
              open ? "Collapse pinned message" : "Open pinned message"
            }
            hitSlop={8}
            style={{
              width: 24,
              height: 24,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {open ? (
              <ChevronUp size={18} color={theme.colors.text3} />
            ) : (
              <ChevronDown size={18} color={theme.colors.text3} />
            )}
          </Pressable>
        </View>
        <View
          style={{ paddingHorizontal: 12, paddingTop: 4, paddingBottom: 8 }}
        >
          {!folded && nameLine}
          <Text
            numberOfLines={folded ? 1 : undefined}
            style={{ fontSize: 15, lineHeight: 23 }}
          >
            <RichTextMessage text={text} facets={facets} />
          </Text>
        </View>
      </Pressable>
    );
  }

  return (
    <View
      style={{
        backgroundColor: raised ? theme.colors.surface2 : theme.colors.surface1,
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 4,
          paddingHorizontal: 8,
          paddingTop: 8,
          paddingLeft: 70,
        }}
      >
        <Pin size={16} color={theme.colors.text3} />
        <Text
          weight="medium"
          style={{
            flex: 1,
            fontSize: 13,
            lineHeight: 17,
            color: theme.colors.text2,
          }}
        >
          Pinned
        </Text>
        {unpinButton}
      </View>
      <View
        style={{ flexDirection: "row", gap: 12, padding: 12, paddingTop: 8 }}
      >
        <Avatar src={avatar} name={name} size={42} />
        <View style={{ flex: 1, minWidth: 0 }}>
          {nameLine}
          <Text style={{ fontSize: 15, lineHeight: 23 }}>
            <RichTextMessage text={text} facets={facets} />
          </Text>
        </View>
      </View>
    </View>
  );
}

// The composer's three states from the design: signed out (the field opens
// login, "Verified users can write messages. Sign in"), signed in but not
// verified while chat is verified-only ("... Verify now", to the node's
// verifyUrl), and able to write (the field alone).
function ComposerNotice({
  text,
  linkLabel,
  onPress,
  style,
}: {
  text: string;
  linkLabel?: string;
  onPress?: () => void;
  style?: TextStyle;
}) {
  const { theme } = useTheme();
  return (
    <Text
      style={[
        {
          fontSize: 13,
          lineHeight: 17,
          color: theme.colors.text2,
          textAlign: "center",
          marginTop: -6,
        },
        style ?? {},
      ]}
    >
      {text}
      {linkLabel && onPress ? (
        <>
          {" "}
          <Text
            onPress={onPress}
            style={{
              fontSize: 13,
              lineHeight: 17,
              color: theme.colors.primary,
            }}
          >
            {linkLabel}
          </Text>
        </>
      ) : null}
    </Text>
  );
}

// Signed out. In the network's PDS login mode the two ways in are laid out
// as buttons (the design: the network's own sign-in, then OAuth for a
// Bluesky / AT Protocol account, which is the only way an outside verified
// account can chat); otherwise the field opens the login modal.
function ComposerSignedOut({ message }: { message: string }) {
  const { theme } = useTheme();
  const openLoginModal = useStore((state) => state.openLoginModal);
  const pdsMode = useBrandingAsset("loginMode")?.data === "pds";
  const quickLogin = useBrandingAsset("quickLogin")?.data === "on";
  const networkName = useNetworkName();
  const networkLabel =
    useBrandingAsset("signInNetworkLabel")?.data?.trim() ||
    `Sign in with your ${networkName} account`;
  const networkHint =
    useBrandingAsset("signInNetworkHint")?.data?.trim() ||
    (quickLogin ? `Authenticate with ${networkName} Identity` : "");
  if (!pdsMode) {
    return (
      <>
        <ComposerPlaceholder onPress={() => openLoginModal()} />
        <ComposerNotice
          text={message}
          linkLabel="Sign in"
          onPress={() => openLoginModal()}
        />
      </>
    );
  }
  return (
    <View style={{ gap: 10 }}>
      <ComposerNotice text={message} style={{ marginTop: 0 }} />
      <Button
        variant="accent"
        width="full"
        style={{ height: 44 }}
        onPress={() => openLoginModal()}
      >
        {networkLabel}
      </Button>
      {networkHint ? (
        <Text
          style={{
            fontSize: 13,
            lineHeight: 17,
            color: theme.colors.text2,
            textAlign: "center",
            marginTop: -4,
          }}
        >
          {networkHint}
        </Text>
      ) : null}
      <OAuthLoginButton
        onPress={() => openLoginModal(undefined, { oauth: true })}
      />
    </View>
  );
}

// A look-alike of the composer field for the states that can't take input.
function ComposerPlaceholder({ onPress }: { onPress?: () => void }) {
  const { theme } = useTheme();
  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      accessibilityRole={onPress ? "button" : undefined}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        height: 44,
        paddingLeft: 12,
        paddingRight: 12,
        borderRadius: 10,
        backgroundColor: theme.colors.surface2,
      }}
    >
      <MessageIcon size={18} color={theme.colors.text3} />
      <Text style={{ fontSize: 15, color: theme.colors.text3, marginLeft: 4 }}>
        Write your message...
      </Text>
    </Pressable>
  );
}

function CardChatPanel({
  fill,
  raised = false,
  bare = false,
}: {
  fill: boolean;
  /** Inside the phone chat sheet: the pinned card one surface step up. */
  raised?: boolean;
  /** No "Live chat" heading (the sheet draws its own header). */
  bare?: boolean;
}) {
  const { theme } = useTheme();
  const agent = usePDSAgent();
  // Signed out for now, but the network's broker hasn't answered yet: the
  // page paints without waiting for it, so the composer shows a loader
  // rather than sign-in buttons that would vanish a moment later.
  const brokerSettled = useStore((state) => state.brokerSettled);
  const emojiData = useEmojiData();
  const customEmoji: any[] = [];
  const access = useAccessStatus();
  const verifiedOnly = !!access?.chatVerifiedOnly;
  const lockedOut = useChatLockedOut();
  // Locked out and not even on the network: a different message and link
  // (apply for an account) than "verify your account".
  const nonMember = lockedOut && access?.networkMember === false;
  const nonMemberMessage =
    useBrandingAsset("chatNonMemberMessage")?.data?.trim() ||
    "Only members can write messages.";
  const applyUrl = useBrandingAsset("networkApplyUrl")?.data?.trim();
  const applyLinkLabel =
    useBrandingAsset("applyLinkLabel")?.data?.trim() || "Apply for access";
  const openExternalLink = useOpenExternal();
  const verifyUrl = useBrandingAsset("verifyUrl")?.data?.trim();
  const verifiedOnlyMessage =
    useBrandingAsset("chatVerifiedOnlyMessage")?.data?.trim() ||
    "Verified users can write messages.";
  const verifyLinkLabel =
    useBrandingAsset("verifyLinkLabel")?.data?.trim() || "Verify now";

  return (
    <View
      style={{
        flex: fill ? 1 : undefined,
        height: fill ? undefined : 560,
        padding: bare ? 16 : 20,
        paddingTop: bare ? 0 : 20,
        gap: bare ? 12 : 16,
      }}
    >
      {!bare && (
        <Text weight="semibold" style={{ fontSize: 15, lineHeight: 23 }}>
          Live chat
        </Text>
      )}
      <PinnedCard raised={raised} collapsible={bare} />
      <View style={{ flex: 1, minHeight: 0 }}>
        <Chat hideSystemMessages />
      </View>
      {agent?.did && nonMember ? (
        <>
          <ComposerPlaceholder />
          <ComposerNotice
            text={nonMemberMessage}
            linkLabel={applyUrl ? applyLinkLabel : undefined}
            onPress={applyUrl ? () => openExternalLink(applyUrl) : undefined}
          />
        </>
      ) : agent?.did && lockedOut ? (
        <>
          <ComposerPlaceholder />
          <ComposerNotice
            text={verifiedOnlyMessage}
            linkLabel={verifyUrl ? verifyLinkLabel : undefined}
            onPress={verifyUrl ? () => Linking.openURL(verifyUrl) : undefined}
          />
        </>
      ) : agent?.did ? (
        <ChatBox
          emojiData={emojiData}
          // The timeline composer: a 44px field, no send button (Enter
          // sends), a chat glyph at the left in place of the badge picker.
          chatBoxStyle={{
            borderRadius: 10,
            backgroundColor: theme.colors.surface2,
            borderWidth: 0,
            height: 44,
            paddingLeft: 12,
            gap: 4,
          }}
          placeholder="Write your message..."
          hideSendButton
          hideToolbar
          leftSlot={<MessageIcon size={18} color={theme.colors.text3} />}
          emojiPicker={(isOpen, onClose, onSelect) => (
            <EmojiPicker
              isOpen={isOpen}
              onClose={onClose}
              onSelect={onSelect}
              customEmoji={customEmoji}
            />
          )}
        />
      ) : !agent || !brokerSettled ? (
        <View
          style={[
            zero.layout.flex.row,
            zero.layout.flex.center,
            {
              padding: 12,
              borderRadius: 10,
              backgroundColor: theme.colors.surface2,
            },
          ]}
        >
          <Loader size="large" />
        </View>
      ) : (
        <ComposerSignedOut
          message={
            verifiedOnly ? verifiedOnlyMessage : "Sign in to write messages."
          }
        />
      )}
    </View>
  );
}

export function StreamCardLayout({
  src,
  extraProps,
  onTeleport,
}: {
  src: string;
  extraProps: Partial<PlayerProps>;
  onTeleport?: (targetHandle: string, targetDID: string) => void;
}) {
  const { theme } = useTheme();
  // Measure the content area rather than the window: the app's sidebar
  // takes a slice of the window on web.
  const [contentWidth, setContentWidth] = useState(0);
  const onLayout = (e: LayoutChangeEvent) =>
    setContentWidth(e.nativeEvent.layout.width);
  // Feed + chat + the hairline between them; the social shell sizes its
  // content column to exactly this, and the chat widens on big windows.
  const { width: windowWidth } = useWindowDimensions();
  const chatWidth = chatWidthFor(windowWidth);
  const twoColumn = contentWidth >= FEED_WIDTH + chatWidth + 1;
  const [chatOpen, setChatOpen] = useState(false);
  const [videoHeight, setVideoHeight] = useState(0);
  const scrollRef = useRef<ScrollView>(null);
  const openChat = () => {
    scrollRef.current?.scrollTo({ y: 0, animated: true });
    setChatOpen(true);
  };
  // In the social shell the nav rail's own border is the feed's left edge.
  const socialShell = useSocialShell();
  // Tell the shell this page wants the feed + chat column while mounted
  // (route names alone misfire, e.g. on the return from login).
  const setWideColumn = useStore((s) => s.setWideColumn);
  useEffect(() => {
    setWideColumn(true);
    return () => setWideColumn(false);
  }, [setWideColumn]);

  if (twoColumn) {
    return (
      <View
        onLayout={onLayout}
        style={{
          flex: 1,
          flexDirection: "row",
          justifyContent: socialShell ? "flex-start" : "center",
          backgroundColor: theme.colors.background,
        }}
      >
        {!socialShell && <Hairline vertical />}
        <ScrollView
          style={{ width: FEED_WIDTH, flexGrow: 0 }}
          contentContainerStyle={{ paddingBottom: 40 }}
        >
          <CardHeader />
          <Hairline />
          <PostCard src={src} extraProps={extraProps} onTeleport={onTeleport} />
          <Hairline />
        </ScrollView>
        <Hairline vertical />
        <View style={{ width: chatWidth, alignSelf: "stretch" }}>
          <CardChatPanel fill />
        </View>
      </View>
    );
  }

  // Narrow (the phone design): the player pinned edge to edge under the
  // header, the post details scrolling beneath it with the chat minimized
  // to its last two messages; opening the chat slides a sheet up over the
  // details, the way a mobile browser shows a video's comments.
  return (
    <View
      onLayout={onLayout}
      style={{
        flex: 1,
        alignItems: "center",
        backgroundColor: theme.colors.background,
      }}
    >
      <View style={{ flex: 1, width: "100%", maxWidth: FEED_WIDTH }}>
        <CardHeader />
        <Hairline />
        <View style={{ flex: 1, minHeight: 0 }}>
          {/* Everything scrolls, the video included, so small phones can
              reach the chat; the sheet opens under the video, which is
              scrolled back into view first. */}
          <ScrollView
            ref={scrollRef}
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingBottom: 28 }}
          >
            <View
              onLayout={(e: LayoutChangeEvent) =>
                setVideoHeight(e.nativeEvent.layout.height)
              }
            >
              <PlayerEmbed
                src={src}
                extraProps={extraProps}
                onTeleport={onTeleport}
                rounded={false}
              />
            </View>
            <PostCard
              src={src}
              extraProps={extraProps}
              onTeleport={onTeleport}
              phone
            />
            <Hairline />
            <MiniChat onOpen={openChat} />
          </ScrollView>
          <ChatSheet
            open={chatOpen}
            top={videoHeight}
            onClose={() => setChatOpen(false)}
          />
        </View>
      </View>
    </View>
  );
}

// The minimized chat on phones: a filled panel with the count, the pinned
// message (two lines), the last four messages one line each — avatar and
// text, links live — and a "Write a message…" link. Tapping anywhere opens
// the chat sheet.
function MiniChat({ onOpen }: { onOpen: () => void }) {
  const { theme } = useTheme();
  const chat = useLivestreamStore((x) => x.chat) ?? [];
  const pinned = useLivestreamStore((x) => x.pinnedComment);
  const pinnedMsg: any = pinned?.message;
  const recent = useMemo(
    () =>
      chat
        .filter(
          (m: any) =>
            m.author?.did !== "did:sys:system" && m.uri !== pinnedMsg?.uri,
        )
        .slice(-4),
    [chat, pinnedMsg?.uri],
  );
  const dids = useMemo(
    () =>
      [pinnedMsg, ...recent]
        .map((m: any) => m?.author?.did)
        .filter(Boolean) as string[],
    [recent, pinnedMsg],
  );
  const profiles = useAvatars(dids);
  const row = (m: any, lines: number) => {
    const author = m.author ?? {};
    const profile = profiles[author.did];
    return (
      <View
        key={m.uri ?? m.cid}
        style={{ flexDirection: "row", gap: 8, alignItems: "flex-start" }}
      >
        <View style={{ paddingTop: 0 }}>
          <Avatar
            src={profile?.avatar || author.avatar}
            name={author.displayName || author.handle}
            size={24}
          />
        </View>
        <Text
          numberOfLines={lines}
          style={{ flex: 1, fontSize: 15, lineHeight: 23 }}
        >
          <RichTextMessage
            text={m.record?.text ?? ""}
            facets={m.record?.facets ?? []}
          />
        </Text>
      </View>
    );
  };
  const count = chat.filter(
    (m: any) => m.author?.did !== "did:sys:system",
  ).length;
  return (
    <Pressable
      onPress={onOpen}
      accessibilityRole="button"
      accessibilityLabel="Open live chat"
      style={{
        marginHorizontal: 8,
        marginTop: 8,
        padding: 12,
        paddingTop: 8,
        borderRadius: 8,
        gap: 8,
        backgroundColor: theme.colors.surface2,
      }}
    >
      <Text style={{ fontSize: 15, lineHeight: 23 }}>
        <Text weight="semibold" style={{ fontSize: 15 }}>
          Live chat
        </Text>
        {count > 0 ? (
          <Text style={{ fontSize: 15, color: theme.colors.text2 }}>
            {"  " + count}
          </Text>
        ) : null}
      </Text>
      {pinnedMsg ? row(pinnedMsg, 2) : null}
      {recent.map((m: any) => row(m, 1))}
      <Text
        onPress={onOpen}
        style={{ fontSize: 15, lineHeight: 23, color: theme.colors.primary }}
      >
        Write a message...
      </Text>
    </Pressable>
  );
}

// The maximized chat on phones: a sheet that slides up under the video
// (fast, and out again), with a grabber that can be dragged down to close,
// the pinned message, the chat and the composer.
function ChatSheet({
  open,
  top,
  onClose,
}: {
  open: boolean;
  top: number;
  onClose: () => void;
}) {
  const { theme } = useTheme();
  const [height, setHeight] = useState(0);
  // Closed = translated fully below its box; open = 0.
  const translateY = useSharedValue(1000);
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      translateY.value = withTiming(0, { duration: 180 });
    } else {
      translateY.value = withTiming(
        height || 1000,
        { duration: 150 },
        (done) => {
          if (done) runOnJS(setMounted)(false);
        },
      );
    }
  }, [open, height, translateY]);
  // The grabber and header drag the sheet down; past a threshold (or a
  // flick) it closes, else it springs back. The responder system rather
  // than a gesture handler: it follows plain touch and mouse events on
  // every platform.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const pan = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, g) =>
        Math.abs(g.dy) > 4 && Math.abs(g.dy) > Math.abs(g.dx),
      onPanResponderMove: (_, g) => {
        translateY.value = Math.max(0, g.dy);
      },
      onPanResponderRelease: (_, g) => {
        if (g.dy > 80 || g.vy > 0.6) {
          onCloseRef.current();
        } else {
          translateY.value = withTiming(0, { duration: 120 });
        }
      },
      onPanResponderTerminate: () => {
        translateY.value = withTiming(0, { duration: 120 });
      },
    }),
  ).current;
  const style = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));
  if (!mounted) return null;
  return (
    <Animated.View
      onLayout={(e: LayoutChangeEvent) =>
        setHeight(e.nativeEvent.layout.height)
      }
      style={[
        {
          position: "absolute",
          top,
          left: 0,
          right: 0,
          bottom: 0,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          backgroundColor: theme.colors.surface1,
          paddingTop: 8,
          overflow: "hidden",
        },
        style,
      ]}
    >
      <View {...pan.panHandlers}>
        <View style={{ alignItems: "center", paddingVertical: 2 }}>
          <View
            style={{
              width: 36,
              height: 5,
              borderRadius: 100,
              backgroundColor: theme.colors.text1,
            }}
          />
        </View>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            paddingHorizontal: 16,
            height: 41,
          }}
        >
          <Text weight="semibold" style={{ fontSize: 15, lineHeight: 23 }}>
            Live chat
          </Text>
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel="Close live chat"
            style={{
              width: 33,
              height: 33,
              borderRadius: 999,
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: theme.colors.surface2,
            }}
          >
            <X size={16} color={theme.colors.text1} />
          </Pressable>
        </View>
      </View>
      <CardChatPanel fill raised bare />
    </Animated.View>
  );
}
